/**
 * Connected Incident / Community affordance (#206) — the evac-order → both-sides
 * half of the cross-link. Rendered inside an Evacuation Order detail panel; it
 * looks up the incidents the order threads to (via the communities its polygon
 * covers, §4.4 edge) and those communities, and surfaces a contextual jump to
 * each: an incident flies the COP (+ an Evacuation Logistics entry when it carries
 * a linked community), a community flies to its centroid.
 *
 * Per design-council PART IX it renders NOTHING until at least one link resolves —
 * an evacuation polygon with no tracked incident/community (common for a raw
 * ingested order) shows no affordance. Self-contained: navigation rides the same-
 * window `selectOnCop` bridge (#185).
 */
import { useEffect, useState } from 'react';
import type { EvacOrderLinks } from '@tsr/shared-types';
import { fetchEvacOrderLinks } from './api';
import { selectOnCop } from './selectionBridge';
import {
  sortLinkedIncidents,
  sortLinkedCommunities,
  hasOrderConnections,
  incidentHasEvacuationEvidence,
  incidentToSelection,
  evacuationSurfaceSelection,
  communityToSelection,
} from './lib/evacLink';

const HAZARD_LABEL: Record<string, string> = {
  wildfire: 'Wildfire',
  flood: 'Flood',
  ice_storm: 'Ice storm',
  cap_warning: 'CAP warning',
  multi_hazard: 'Multi-hazard',
  other: 'Incident',
};

const EMPTY: EvacOrderLinks = { incidents: [], communities: [] };

export function ConnectedEvacOrderLinks({ featureId }: { featureId: string }): JSX.Element | null {
  const [links, setLinks] = useState<EvacOrderLinks>(EMPTY);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchEvacOrderLinks(featureId, ctl.signal)
      .then((r) =>
        setLinks({
          incidents: sortLinkedIncidents(r.incidents),
          communities: sortLinkedCommunities(r.communities),
        }),
      )
      .catch(() => undefined);
    return () => ctl.abort();
  }, [featureId]);

  // Contextual gate: no threaded incident or community → render nothing.
  if (!hasOrderConnections(links)) return null;

  return (
    <div data-testid="connected-evac-order-links" className="space-y-3">
      {links.incidents.length > 0 && (
        <section className="space-y-1.5">
          <h3 className="text-xs uppercase tracking-wider text-slate-400">
            Connected incident{links.incidents.length > 1 ? `s (${links.incidents.length})` : ''}
          </h3>
          <ul className="space-y-1">
            {links.incidents.map((inc) => (
              <li key={inc.id} className="space-y-1">
                <button
                  type="button"
                  data-testid="connected-incident-btn"
                  onClick={() => selectOnCop(incidentToSelection(inc))}
                  className="flex w-full items-center gap-2 rounded border border-amber-500/50 bg-amber-600/15 px-2 py-1.5 text-left text-[12px] text-amber-100 hover:bg-amber-600/25"
                >
                  <span className="font-mono shrink-0">{inc.incident_number}</span>
                  <span className="min-w-0 flex-1 truncate text-amber-200/80">
                    {HAZARD_LABEL[inc.hazard_type] ?? inc.hazard_type} · {inc.status}
                  </span>
                  <span aria-hidden className="shrink-0 text-amber-300">→</span>
                </button>
                {incidentHasEvacuationEvidence(inc) && (
                  <button
                    type="button"
                    data-testid="connected-incident-evacuation"
                    onClick={() => selectOnCop(evacuationSurfaceSelection(inc))}
                    className="flex w-full items-center justify-center gap-2 rounded bg-rose-600/90 hover:bg-rose-600 border border-rose-500 px-2 py-1 text-[11px] font-medium text-white"
                  >
                    <span aria-hidden>🚁</span> Evacuation Logistics
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {links.communities.length > 0 && (
        <section className="space-y-1.5">
          <h3 className="text-xs uppercase tracking-wider text-slate-400">
            Affected communities{links.communities.length > 1 ? ` (${links.communities.length})` : ''}
          </h3>
          <ul className="space-y-1">
            {links.communities.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  data-testid="connected-community-btn"
                  onClick={() => selectOnCop(communityToSelection(c))}
                  className="flex w-full items-center gap-2 rounded border border-sky-500/50 bg-sky-600/15 px-2 py-1.5 text-left text-[12px] text-sky-100 hover:bg-sky-600/25"
                >
                  <span aria-hidden className="text-sky-300">⌖</span>
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                  <span aria-hidden className="shrink-0 text-sky-300">→</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
